/**
 * Aggregates all four version strings for display in settings.
 *
 * - Frontend version: injected at build time via Vite define
 * - Backend version: fetched from /health endpoint
 * - Android app version: from Capacitor bridge (if running in WebView)
 * - Gateway version: from WebSocket connection info
 */

import { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../utils/environment';
import { getAppBasePath, getCurrentPreviewPrNumber } from '../utils/previewBuild';

// Injected by Vite at build time (see vite.config.ts `define`)
declare const __FRONTEND_VERSION__: string;
declare const __BUILD_TIME__: string;

export interface VersionInfo {
  frontend: string;
  backend: string | null;
  android: string | null;
  gateway: string | null;
  buildTime: string;
  appBasePath: string;
  previewPrNumber: number | null;
}

/**
 * Try to get the Android app version via Capacitor bridge.
 * Returns null if not running inside the Android wrapper.
 */
async function getAndroidVersion(): Promise<string | null> {
  try {
    // Check if we're running in Capacitor
    const cap = (window as unknown as Record<string, unknown>).Capacitor as
      | { getPlatform?: () => string; Plugins?: Record<string, unknown> }
      | undefined;
    if (!cap?.getPlatform || cap.getPlatform() !== 'android') return null;

    // Try to use the App plugin if available
    const appPlugin = cap.Plugins?.App as
      | { getInfo?: () => Promise<{ version: string; build: string }> }
      | undefined;
    if (appPlugin?.getInfo) {
      const info = await appPlugin.getInfo();
      return `${info.version} (${info.build})`;
    }

    // Fallback: check user agent for version hint
    const ua = navigator.userAgent;
    const match = ua.match(/Talkyn\/([\d.]+)/);
    if (match) return match[1];

    return 'installed';
  } catch {
    return null;
  }
}

/**
 * Fetch backend version from the /health endpoint.
 */
async function fetchBackendVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${getApiUrl()}/health`, {
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) return null;
    const data = await res.json() as { version?: string };
    return data.version ?? null;
  } catch {
    return null;
  }
}

export function useVersionInfo(gatewayVersion?: string | null): VersionInfo {
  const [backend, setBackend] = useState<string | null>(null);
  const [android, setAndroid] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [be, av] = await Promise.all([
      fetchBackendVersion(),
      getAndroidVersion(),
    ]);
    setBackend(be);
    setAndroid(av);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    frontend: typeof __FRONTEND_VERSION__ !== 'undefined' ? __FRONTEND_VERSION__ : 'dev',
    backend,
    android,
    gateway: gatewayVersion ?? null,
    buildTime: typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '',
    appBasePath: getAppBasePath(),
    previewPrNumber: getCurrentPreviewPrNumber(),
  };
}
